// src/components/onboarding/VoiceFeedbackFirstRunCoachmark.tsx
//
// Per dev-sync note 6793d1bb — the *first* time the leads-mode voice coach
// fires for a user, surface a small coachmark anchored to the Settings
// rail button telling them voice feedback is on and pointing them at the
// gear so they can mute / change voice mode. Auto-dismisses; persistence
// of the "seen" flag lives in `@/lib/voiceFeedback/firstRunNotice`.
//
// Anchor strategy: the SettingsPopover trigger button is decorated with
// `data-onboarding-anchor="settings"`. We re-query on each show so the
// coachmark stays correct if the sidebar collapses / expands or layout
// shifts (we do not subscribe to resize because the popover is short-lived
// and a one-time miss is acceptable).

import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { VOICE_FEEDBACK_FIRST_RUN_EVENT } from "@/lib/voiceFeedback/firstRunNotice";
import { useOrgSettings } from "@/hooks/useOrgSettings";

type AnchorRect = {
  top: number;
  right: number;
  bottom: number;
  left: number;
  width: number;
  height: number;
};

// 9 seconds gives the user time to read + react without lingering when
// they switch tabs. They can also dismiss manually.
const AUTO_DISMISS_MS = 9000;
const FALLBACK_TOP_PX = 16;
const FALLBACK_LEFT_PX = 16;
const COACHMARK_OFFSET_PX = 12;
const COACHMARK_WIDTH_PX = 280;

function readAnchorRect(): AnchorRect | null {
  if (typeof document === "undefined") return null;
  const el = document.querySelector<HTMLElement>('[data-onboarding-anchor="settings"]');
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return {
    top: r.top,
    right: r.right,
    bottom: r.bottom,
    left: r.left,
    width: r.width,
    height: r.height,
  };
}

export function VoiceFeedbackFirstRunCoachmark() {
  const { appDisplayName } = useOrgSettings();
  const [visible, setVisible] = useState(false);
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);

  const dismiss = useCallback(() => {
    setVisible(false);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => {
      setAnchor(readAnchorRect());
      setVisible(true);
    };
    window.addEventListener(VOICE_FEEDBACK_FIRST_RUN_EVENT, handler);
    return () => window.removeEventListener(VOICE_FEEDBACK_FIRST_RUN_EVENT, handler);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const t = window.setTimeout(() => setVisible(false), AUTO_DISMISS_MS);
    return () => window.clearTimeout(t);
  }, [visible]);

  // Re-read the anchor on every visible mount so transitions land cleanly
  // even if the sidebar expanded between the speak event and React commit.
  useLayoutEffect(() => {
    if (!visible) return;
    setAnchor(readAnchorRect());
  }, [visible]);

  if (!visible || typeof document === "undefined") return null;

  // Position to the right of the rail button (rail sits on the left). If
  // we can't find the anchor (e.g. mobile sidebar collapsed behind a
  // hamburger) we fall back to a top-left corner placement so the user
  // still sees the message even without the arrow.
  const hasAnchor = anchor !== null;
  const tipTop = hasAnchor
    ? Math.max(FALLBACK_TOP_PX, anchor.top + anchor.height / 2 - 28)
    : FALLBACK_TOP_PX;
  const tipLeft = hasAnchor
    ? anchor.right + COACHMARK_OFFSET_PX
    : FALLBACK_LEFT_PX;

  return createPortal(
    <div
      role="dialog"
      aria-label="Voice feedback is on"
      className="fixed z-[9300] pointer-events-auto"
      style={{
        top: tipTop,
        left: tipLeft,
        width: COACHMARK_WIDTH_PX,
        maxWidth: `calc(100vw - ${tipLeft + 16}px)`,
      }}
    >
      <div className="relative rounded-2xl border border-indigo-300 bg-indigo-50 px-4 py-3 text-sm text-indigo-900 shadow-xl dark:border-indigo-400 dark:bg-indigo-950/80 dark:text-indigo-100">
        {hasAnchor ? (
          <span
            aria-hidden="true"
            className="absolute -left-2 top-6 h-3 w-3 rotate-45 border-l border-b border-indigo-300 bg-indigo-50 dark:border-indigo-400 dark:bg-indigo-950"
          />
        ) : null}
        <div className="flex items-start gap-2">
          <span aria-hidden="true" className="text-base leading-none">🔊</span>
          <div className="flex-1">
            <p className="font-semibold">Voice feedback is on</p>
            <p className="mt-1 text-xs leading-relaxed">
              {appDisplayName} will speak coaching tips while you work leads. Open the
              Settings gear (←) and pick <strong>Voice</strong> to mute it or change
              what gets spoken.
            </p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss voice feedback notice"
            className="ml-1 -mr-1 -mt-1 rounded p-1 text-indigo-700 hover:bg-indigo-100 hover:text-indigo-900 dark:text-indigo-200 dark:hover:bg-indigo-900"
          >
            ✕
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
